import { getApiUrl } from './api'
import { useAuthStore } from './auth'
import type { TrackWatchRequest } from '../../../shared/types/watch-history.types'

interface VidsrcMovieData {
  tmdb_id: number
  title: string
  poster_path?: string | null
  backdrop_path?: string | null
  overview?: string | null
  release_date?: string | null
  vote_average?: number | null
  source_type: 'vidsrc'
  source_id?: string | null // TMDB ID as string
  stream_url?: string | null
  quality?: string | null
  gdrive_file_id?: string | null
}

class VidsrcTracker {
  private updateInterval: NodeJS.Timeout | null = null
  private lastPosition = 0
  private isPlaying = false
  private videoElement: HTMLVideoElement | null = null
  private movieData: VidsrcMovieData | null = null
  
  startTracking(movieData: VidsrcMovieData, videoElement: HTMLVideoElement) {
    this.movieData = movieData
    this.videoElement = videoElement
    
    console.log('🎬 VidsrcTracker: Starting tracking for', movieData.title)
    
    // Listen to video state changes (ONLY track when actually playing)
    videoElement.addEventListener('play', this.handlePlay)
    videoElement.addEventListener('playing', this.handlePlaying)
    videoElement.addEventListener('pause', this.handlePause)
    videoElement.addEventListener('ended', this.handleEnded)
    videoElement.addEventListener('waiting', this.handleWaiting)
    videoElement.addEventListener('seeking', this.handleSeeking)
    videoElement.addEventListener('seeked', this.handleSeeked)
    
    // Load saved position and resume
    this.loadSavedPosition()
    
    // Setup periodic tracking
    this.setupPeriodicTracking()
    
    // Track initial watch
    this.trackInitialWatch()
  }
  
  private async loadSavedPosition() {
    if (!this.movieData) return
    
    try {
      const headers = useAuthStore.getState().getAuthHeaders()
      const response = await fetch(getApiUrl('WATCH_HISTORY_RECENT'), {
        headers
      })
      const data = await response.json()
      
      if (data.success && data.data && Array.isArray(data.data)) {
        // Find the entry for this movie with vidsrc source
        const entry = data.data.find((e: any) => 
          e.tmdb_id === this.movieData?.tmdb_id && 
          e.source_type === 'vidsrc' &&
          e.playback_position_seconds > 0
        )
        
        if (entry && this.videoElement) {
          const savedPosition = entry.playback_position_seconds
          this.videoElement.currentTime = savedPosition
          this.lastPosition = savedPosition
          console.log(`▶️ VidsrcTracker: Resuming from ${savedPosition}s`)
        }
      }
    } catch (error) {
      console.error('VidsrcTracker: Failed to load saved position:', error)
    }
  }
  
  private handlePlay = () => {
    console.log('🎬 VidsrcTracker: Video play event')
    this.isPlaying = true
  }
  
  private handlePlaying = () => {
    console.log('▶️ VidsrcTracker: Video playing (after buffering)')
    this.isPlaying = true
  }
  
  private handlePause = () => {
    console.log('⏸️ VidsrcTracker: Video paused')
    this.isPlaying = false
    // Save position immediately on pause
    this.saveCurrentPosition()
  }
  
  private handleEnded = () => {
    console.log('✅ VidsrcTracker: Video ended')
    this.isPlaying = false
    // Mark as completed
    this.markAsCompleted()
  }
  
  private handleWaiting = () => {
    console.log('⏳ VidsrcTracker: Video buffering')
    this.isPlaying = false
  }
  
  private handleSeeking = () => {
    this.isPlaying = false
  }
  
  private handleSeeked = () => {
    // Check if video is playing after seek
    if (this.videoElement && !this.videoElement.paused) {
      this.isPlaying = true
    }
  }
  
  private setupPeriodicTracking() {
    // Update every 30 seconds when video is playing
    console.log('⏰ VidsrcTracker: Setting up periodic tracking (every 30s)')
    
    // Clear any existing interval first
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
    }
    
    this.updateInterval = setInterval(() => {
      // Always check if we can update (will be validated inside updateProgress)
      // Only skip if video is definitely ended or doesn't exist
      if (!this.videoElement) {
        console.log('⏰ VidsrcTracker: Periodic update skipped - no video element')
        return
      }
      
      if (this.videoElement.ended) {
        console.log('⏰ VidsrcTracker: Periodic update skipped - video ended')
        return
      }
      
      // Always try to update unless video is ended
      // (updateProgress will validate duration and other requirements)
      console.log('⏰ VidsrcTracker: Periodic update triggered (30s interval)', {
        paused: this.videoElement.paused,
        currentTime: Math.floor(this.videoElement.currentTime),
        duration: Math.floor(this.videoElement.duration),
        readyState: this.videoElement.readyState
      })
      this.updateProgress()
    }, 30000) // 30 seconds
  }
  
  private async trackInitialWatch() {
    if (!this.movieData) return
    
    // Track that user opened this movie
    try {
      const trackData: TrackWatchRequest = {
        tmdb_id: this.movieData.tmdb_id,
        title: this.movieData.title,
        poster_path: this.movieData.poster_path,
        backdrop_path: this.movieData.backdrop_path,
        overview: this.movieData.overview,
        release_date: this.movieData.release_date,
        vote_average: this.movieData.vote_average,
        source_type: 'vidsrc',
        source_id: this.movieData.source_id || this.movieData.tmdb_id.toString(),
        stream_url: this.movieData.stream_url || null,
        playback_position_seconds: 0,
        total_duration_seconds: 0,
        quality: this.movieData.quality || null,
        gdrive_file_id: this.movieData.gdrive_file_id || null
      }
      
      const headers = {
        'Content-Type': 'application/json',
        ...useAuthStore.getState().getAuthHeaders()
      }
      
      await fetch(getApiUrl('WATCH_HISTORY_TRACK'), {
        method: 'POST',
        headers,
        body: JSON.stringify(trackData)
      })
      console.log('✅ VidsrcTracker: Initial watch tracked')
    } catch (error) {
      console.error('❌ VidsrcTracker: Failed to track initial watch:', error)
    }
  }
  
  private async updateProgress() {
    if (!this.movieData || !this.videoElement) {
      return
    }
    
    // Validate video element state
    if (this.videoElement.ended) {
      return
    }
    
    // Check if duration is available and valid
    if (!this.videoElement.duration || isNaN(this.videoElement.duration) || this.videoElement.duration === 0) {
      console.log('⏰ VidsrcTracker: Skipping update - duration not available')
      return
    }
    
    const currentTime = this.videoElement.currentTime
    const duration = this.videoElement.duration
    
    // Only update if position changed significantly (more than 5 seconds)
    if (Math.abs(currentTime - this.lastPosition) < 5) {
      return
    }
    
    // Calculate watch duration before updating lastPosition
    const watchDuration = Math.max(0, currentTime - this.lastPosition)
    this.lastPosition = currentTime
    
    try {
      const trackData: TrackWatchRequest = {
        tmdb_id: this.movieData.tmdb_id,
        title: this.movieData.title,
        poster_path: this.movieData.poster_path,
        backdrop_path: this.movieData.backdrop_path,
        overview: this.movieData.overview,
        release_date: this.movieData.release_date,
        vote_average: this.movieData.vote_average,
        playback_position_seconds: Math.floor(currentTime),
        total_duration_seconds: Math.floor(duration),
        watch_duration_seconds: Math.floor(watchDuration),
        source_type: 'vidsrc',
        source_id: this.movieData.source_id || this.movieData.tmdb_id.toString(),
        stream_url: this.movieData.stream_url || null,
        quality: this.movieData.quality || null,
        gdrive_file_id: this.movieData.gdrive_file_id || null
      }
      
      const headers = {
        'Content-Type': 'application/json',
        ...useAuthStore.getState().getAuthHeaders()
      }
      
      const response = await fetch(getApiUrl('WATCH_HISTORY_TRACK'), {
        method: 'POST',
        headers,
        body: JSON.stringify(trackData)
      })
      
      if (!response.ok) {
        console.error('❌ VidsrcTracker: Progress update failed:', response.status, response.statusText)
        const errorText = await response.text().catch(() => 'Unknown error')
        console.error('Error details:', errorText)
        return
      }
      
      console.log(`✅ VidsrcTracker: Progress updated (${Math.floor(currentTime)}s / ${Math.floor(duration)}s)`)
    } catch (error) {
      console.error('❌ VidsrcTracker: Failed to save progress:', error)
      if (error instanceof Error) {
        console.error('Error message:', error.message)
      }
    }
  }
  
  private async saveCurrentPosition() {
    await this.updateProgress()
  }
  
  private async markAsCompleted() {
    if (!this.videoElement || !this.movieData) return
    
    const watchedPercentage = (this.videoElement.currentTime / this.videoElement.duration) * 100
    
    if (watchedPercentage >= 90) {
      try {
        const headers = {
          'Content-Type': 'application/json',
          ...useAuthStore.getState().getAuthHeaders()
        }
        
        await fetch(getApiUrl('WATCH_HISTORY_COMPLETE'), {
          method: 'POST',
          headers,
          body: JSON.stringify({
            tmdb_id: this.movieData.tmdb_id,
            completed: true
          })
        })
        console.log('✅ VidsrcTracker: Movie marked as completed (90%+ watched)')
      } catch (error) {
        console.error('VidsrcTracker: Failed to mark as completed:', error)
      }
    }
  }
  
  stopTracking() {
    console.log('⏹️ VidsrcTracker: Stopping tracking')
    
    if (this.updateInterval) {
      clearInterval(this.updateInterval)
      this.updateInterval = null
    }
    
    if (this.videoElement) {
      this.videoElement.removeEventListener('play', this.handlePlay)
      this.videoElement.removeEventListener('playing', this.handlePlaying)
      this.videoElement.removeEventListener('pause', this.handlePause)
      this.videoElement.removeEventListener('ended', this.handleEnded)
      this.videoElement.removeEventListener('waiting', this.handleWaiting)
      this.videoElement.removeEventListener('seeking', this.handleSeeking)
      this.videoElement.removeEventListener('seeked', this.handleSeeked)
      
      // Save final position
      this.saveCurrentPosition()
    }
    
    this.videoElement = null
    this.movieData = null
    this.isPlaying = false
    this.lastPosition = 0
  }
  
  updateGDriveInfo(gdriveFileId: string, gdriveStreamUrl: string) {
    if (this.movieData) {
      this.movieData.gdrive_file_id = gdriveFileId
      this.movieData.stream_url = gdriveStreamUrl
      console.log('✅ VidsrcTracker: Updated with GDrive info')
    }
  }
}

export const vidsrcTracker = new VidsrcTracker()

